import React from 'react';
import Popover from './Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag, faClock, faCalendarDay, faCalendar, faPersonChalkboard,
    faBookOpenReader, faLocationDot, faSchool, faChair, faCopy, faBookOpen, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import {
    getDaysOfWeek,
    formatTime,
    formatDateRange,
    createMarkup, formatDateRangeDuration
} from '../utils/formatters';

const MeetingTime = ({ section, onlinePop }) => {
    return (
        <div className="md:grid md:grid-cols-5 md:gap-x-4 text-sm p-3 border-t border-gray-100 first:border-t-0">
            <div className="flex items-center md:block mb-2 md:mb-0">
                <FontAwesomeIcon icon={faClock} className="mr-2 text-gray-500 w-4 md:hidden" />
                <span className="md:hidden font-semibold mr-2 w-20 inline-block">Time:</span>
                <span className="font-medium text-gray-900">
                    {section.beginTime && section.endTime ? `${formatTime(section.beginTime)} - ${formatTime(section.endTime)}` : `${section.weeklyHrs} Hrs/Wk`}
                </span>
            </div>
            <div className="flex items-center md:block mb-2 md:mb-0">
                <FontAwesomeIcon icon={faCalendarDay} className="mr-2 text-gray-500 w-4 md:hidden" />
                <span className="md:hidden font-semibold mr-2 w-20 inline-block">Days:</span>
                <span className="text-gray-700">{getDaysOfWeek(section) || 'TBA'}</span>
            </div>
            <div className="flex items-center md:block mb-2 md:mb-0">
                <FontAwesomeIcon icon={faLocationDot} className="mr-2 text-gray-500 w-4 md:hidden" />
                <span className="md:hidden font-semibold mr-2 w-20 inline-block">Location:</span>
                {onlinePop && onlinePop.hasOwnProperty(section.stvbldgDesc) ? (<Popover description={section.stvbldgDesc} content={onlinePop[section.stvbldgDesc]}>
                    <span className="text-blue-600 hover:underline cursor-pointer">{section.stvbldgDesc}</span>
                </Popover>) : (<span className="text-gray-700">{section.stvbldgDesc} {section.roomCode === 'Needs Assignment' ? '' : `- ${section.roomCode}`}</span>)}
            </div>
            <div className="flex items-center md:block mb-2 md:mb-0">
                <FontAwesomeIcon icon={faBookOpenReader} className="mr-2 text-gray-500 w-4 md:hidden" />
                <span className="md:hidden font-semibold mr-2 w-20 inline-block">Format:</span>
                <span className="text-gray-700">{section.schdDesc || 'N/A'}</span>
            </div>
            <div className="flex items-center md:block"><FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-500 w-4 md:hidden" />
                <span className="md:hidden font-semibold mr-2 w-20 inline-block">Duration:</span>
                <span className="text-gray-700">{formatDateRangeDuration(section.ptrmStartDate, section.ptrmEndDate)}</span>
            </div>
        </div>
    );
};

function CRNCentricSectionCard({ crn, sections, onlinePop, ...props }) {
    const item = sections[0];
    const {
        setDrawerVisible, setSelectedCRN,
        setInstructorDrawerOpen, setInstructorData
    } = props;

    // Helper instructor data for the drawer
    const instructorInfo = {
        primary: { name: item.pfaName, email: item.pfaEmail },
        secondary: item.sfaName?.split(',').map((name, idx) => ({
            name: name.trim(),
            email: item.sfaEmail?.split(',')[idx]?.trim()
        })).filter(inst => inst.name)
    };

    // Helper status badge text and color
    const getStatusInfo = () => {
        switch (item.fwSectOpenInd) {
            case 'C': return { text: 'Closed', style: 'bg-red-100 text-red-800' };
            case 'O': return { text: 'Open', style: 'bg-green-100 text-green-800' };
            case 'W': return { text: 'Waitlist', style: 'bg-blue-100 text-blue-800' };
            case 'R': return { text: 'Reserved', style: 'bg-yellow-100 text-yellow-800' };
            default: return { text: 'Unknown', style: 'bg-gray-100 text-gray-800' };
        }
    };
    const status = getStatusInfo();

    // enrollment percentage for the progress bar
    const enrollmentPercentage = Math.min((parseInt(item.enrl || 0) / parseInt(item.maxEnrl || 1)) * 100, 100);
    const waitlistPercentage = Math.min((parseInt(item.waitCount || 0) / parseInt(item.waitCapacity || 1)) * 100, 100);

    const criticalDates = [
        { label: "Last day to add", value: formatDateRange(item.lastDateToEnroll) },
        { label: "Drop with refund", value: formatDateRange(item.refundDate) },
        { label: "Drop without a \"W\"", value: formatDateRange(item.dropNoWDate) },
        { label: "Drop with a \"W\"", value: formatDateRange(item.dropWDate) },
    ].filter(d => d.value && d.value !== 'N/A');

    const handleCopy = (textToCopy, type) => {
        if (!textToCopy) return;
        navigator.clipboard.writeText(textToCopy).then(() => {
            alert(`${type} copied to clipboard!`);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            alert(`Could not copy ${type}.`);
        });
    };

    return (
        <div className="border-t border-gray-200">
            <div className="flex flex-col md:flex-row p-4 gap-x-6 gap-y-4 bg-white">

                {/* ====== Part 1: Key Info Sidebar ====== */}
                <div className="w-full md:w-64 flex-shrink-0 space-y-4">
                    {/* CRN with Dates Popover */}
                    <div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${status.style}`}>{status.text}</span>
                        <h4 className="text-lg font-bold text-gray-800 mt-1 flex items-center gap-2">
                            <FontAwesomeIcon icon={faTag} className="text-gray-400" />
                            CRN:
                            <Popover
                                description="Critical Dates"
                                content={
                                    <div className="text-xs space-y-1 p-2">
                                        {criticalDates.length > 0 ? criticalDates.map((d, i) => (
                                            <div key={i} className="flex justify-between items-start">
                                                <span className="mr-2 whitespace-nowrap">{d.label}:</span>
                                                <span className="font-mono text-right">{d.value}</span>
                                            </div>
                                        )) : "No critical dates available."}
                                    </div>
                                }
                            >
                                <span
                                    className="text-blue-600 hover:underline cursor-pointer"
                                    onClick={() => { setSelectedCRN(item); setDrawerVisible(true); }}
                                >
                                    {crn}
                                </span>
                            </Popover>
                        </h4>
                    </div>

                    {/* Instructor with Email Popover */}
                    <div className="text-sm">
                        <div className="font-semibold text-gray-500 flex items-center gap-2">
                            <FontAwesomeIcon icon={faPersonChalkboard} className="text-gray-400" /> Instructor
                        </div>
                        <div className="flex items-center gap-2 pl-6">
                            <Popover description="Instructor Info" content={instructorInfo.primary.email ? `Email: ${instructorInfo.primary.email}` : 'No email available.'}>
                                <div className="text-blue-600 capitalize hover:text-blue-700 hover:underline cursor-pointer" onClick={() => { setInstructorDrawerOpen(true); setInstructorData(instructorInfo); }}>
                                    {instructorInfo.primary.name}
                                    {instructorInfo.secondary?.length > 0 && ` +${instructorInfo.secondary.length}`}
                                </div>
                            </Popover>
                            {/* Copy Icon for Email */}
                            {instructorInfo.primary.email && (
                                <FontAwesomeIcon
                                    icon={faCopy}
                                    className="text-gray-400 hover:text-blue-600 cursor-pointer"
                                    title="Copy instructor email"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevents opening the drawer
                                        handleCopy(instructorInfo.primary.email, 'Email');
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    {/* Campus Location with Address Popover */}
                    <div className="text-sm">
                        <div className="font-semibold text-gray-500 flex items-center gap-2">
                            <FontAwesomeIcon icon={faSchool} className="text-gray-400" /> Campus
                        </div>
                        <div className="flex items-center gap-2 pl-6">
                            <Popover description="Campus Address" content={item.bldgAddress || 'No address available.'}>
                                <div className="text-blue-600 hover:underline cursor-pointer">{item.campDesc || 'N/A'}</div>
                            </Popover>
                            {/* Copy Icon for Address */}
                            {item.bldgAddress && (
                                <FontAwesomeIcon
                                    icon={faCopy}
                                    className="text-gray-400 hover:text-blue-600 cursor-pointer"
                                    title="Copy campus address"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(item.bldgAddress, 'Address');
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-4">
                        {/* Units */}
                        <div className="text-sm">
                            <div className="font-semibold text-gray-500 flex items-center gap-2">
                                <FontAwesomeIcon icon={faBookOpen} className="text-gray-400" /> Units
                            </div>
                            <div className="text-gray-900 pl-6">{item.creditHrLow}</div>
                        </div>
                        {/* Seats */}
                        <div className="text-sm">
                            {parseInt(item.seatsAvail) <= 0 && parseInt(item.maxEnrl) > 0 ? (
                                // "Full" State
                                <div className="font-semibold text-gray-500 flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChair} className="text-gray-400" />
                                        <span>Seats:</span>
                                    </div>
                                    <span className="text-red-600 font-bold">Full</span>
                                </div>
                            ) : (
                                // "Available" State
                                <>
                                    <div className="font-semibold text-gray-500 flex justify-between items-center gap-2">
                                        <div className="flex items-center gap-2">
                                            <FontAwesomeIcon icon={faChair} className="text-gray-400" /> Seats
                                        </div>
                                        <span className="font-normal">{item.enrl || 0} / {item.maxEnrl || 0}</span>
                                    </div>
                                    <div className="h-2 mt-1 bg-gray-200 rounded-full w-full overflow-hidden ml-6">
                                        <div
                                            className={`h-2 rounded-full ${enrollmentPercentage >= 90 ? 'bg-red-500' : enrollmentPercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                            style={{ width: `${enrollmentPercentage}%` }}
                                        ></div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Waitlist Section */}
                        {(parseInt(item.waitCapacity) > 0) && (
                            <div className="text-sm">
                                <div className="font-semibold text-gray-500 flex justify-between items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChair} className="text-blue-400" /> Waitlist
                                    </div>
                                    <span className="font-normal">{item.waitCount || 0} / {item.waitCapacity || 0}</span>
                                </div>
                                <div className="h-2 mt-1 bg-gray-200 rounded-full w-full overflow-hidden ml-6">
                                    <div
                                        className="h-2 rounded-full bg-blue-500"
                                        style={{ width: `${waitlistPercentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ====== Part 2: Meeting Logistics ====== */}
                <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <div className="hidden md:grid md:grid-cols-5 gap-x-4 text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 border-b-2 border-gray-200 bg-gray-100">
                        <div>Time</div><div>Days</div><div>Location</div><div>Format</div><div>Duration</div>
                    </div>
                    {sections.map((section, index) => <MeetingTime key={index} section={section} onlinePop={onlinePop} />)}
                </div>
            </div>

            {/* ====== Part 3: Footer with Badges & Footnotes ====== */}
            <div className="px-4 pb-4 bg-white flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2">
                    {(item.attrchrts || '').split(',').map(a => a.trim()).filter(Boolean).map(attr => {
                        let badgeStyle = "font-medium px-2 py-1 rounded-full text-xs";
                        if (attr === 'UC') return <span key={attr} className={`bg-purple-100 text-purple-800 ${badgeStyle}`}>Transferable UC</span>;
                        if (attr === 'CSU') return <span key={attr} className={`bg-indigo-100 text-indigo-800 ${badgeStyle}`}>Transferable CSU</span>;
                        if (attr === 'ZTC') return <span key={attr} className={`bg-green-100 text-green-800 ${badgeStyle}`}>ZTC</span>;
                        if (attr === 'QR') return <span key={attr} className={`bg-sky-100 text-sky-800 ${badgeStyle}`}>QR</span>;
                        return null;
                    })}
                </div>
                {/* Footnotes */}
                {(() => {
                    const uniqueFootnotes = [...new Set(sections
                        .map(sec => sec.sectionFootnotes)
                        .filter(f => f && f.trim()))];
                    return uniqueFootnotes.length > 0 ? (
                        <div className="max-w-fit text-xs text-gray-600 font-bold text-left sm:text-right">
                            {uniqueFootnotes.map((footnote, i) => (
                                <div key={i} dangerouslySetInnerHTML={createMarkup(footnote)}></div>
                            ))}
                        </div>
                    ) : null;
                })()}
            </div>
        </div>
    );
}
export default CRNCentricSectionCard;